/**
 * Marketing-site API base URL resolution.
 *
 * The marketing site has a small handful of `fetch("/api/…")` calls
 * (Stripe checkout on the pricing pages, lead-capture form
 * submissions). They all assume the API is on the same origin as the
 * marketing SPA, which is true in single-origin previews but is **not**
 * true in production: the marketing site is served from
 * `clauseark.com` / `www.clauseark.com`, while the API lives at
 * `api.clauseark.com`. The web app, the Word add-in, and the API
 * server each have their own subdomain too.
 *
 * Rather than touch every call site, we install a single fetch
 * interceptor at app boot that rewrites any request starting with
 * `/api/` (or exactly `/api`) to the configured API base URL. The
 * configuration comes from the build-time env var `VITE_API_BASE_URL`
 * (e.g. `https://api.clauseark.com`). When unset (dev, integration
 * tests, single-origin previews) the interceptor is a no-op and
 * relative `/api/…` paths resolve against the current origin exactly
 * as they did before.
 *
 * This is an intentional copy of `artifacts/web-app/src/lib/apiBase.ts`
 * — the two artifacts are independent Autoscale deployments and the
 * helper is small enough that hoisting it into a shared package would
 * be more ceremony than value. If you change the rewrite semantics
 * here, mirror the change in the web-app file (and vice-versa) so
 * both sites behave identically against the production API.
 */

// `import.meta.env` is what Vite injects in the browser bundle. When
// this module is loaded by `node:test` directly (no Vite), the env
// hangs off `globalThis.__APIBASE_TEST_ENV__` instead. Both are
// optional; falling back to the empty string makes the interceptor a
// no-op (the historical single-origin behaviour).
type EnvBag = { VITE_API_BASE_URL?: string };
const VITE_ENV =
  (import.meta as unknown as { env?: EnvBag }).env ??
  (globalThis as unknown as { __APIBASE_TEST_ENV__?: EnvBag }).__APIBASE_TEST_ENV__ ??
  {};
const RAW_BASE = (VITE_ENV.VITE_API_BASE_URL as string | undefined) ?? "";

/**
 * Strip a single trailing slash from the configured base URL so
 * concatenating `${base}/api/…` never produces `//api/…`.
 */
function normaliseBase(base: string): string {
  if (!base) return "";
  return base.endsWith("/") ? base.slice(0, -1) : base;
}

const API_BASE = normaliseBase(RAW_BASE.trim());

/**
 * Returns true when the request URL is a same-origin `/api/…` path
 * that the interceptor should rewrite.
 *
 * We deliberately match only string inputs (URL objects and Request
 * instances are passed through untouched). Strings are the format
 * every existing call site uses, and limiting the rewrite to strings
 * means library code that builds full `https://…` URLs is never
 * touched.
 */
export function isRelativeApiPath(input: unknown): input is string {
  if (typeof input !== "string") return false;
  return input === "/api" || input.startsWith("/api/") || input.startsWith("/api?");
}

/**
 * Rewrite a `/api/…` path to the configured production API origin.
 * No-ops when `VITE_API_BASE_URL` is unset.
 */
export function rewriteApiUrl(path: string): string {
  if (!API_BASE) return path;
  if (!isRelativeApiPath(path)) return path;
  return `${API_BASE}${path}`;
}

let installed = false;

/**
 * Install the fetch interceptor. Idempotent — safe to call from
 * `main.tsx` more than once during HMR.
 */
export function installApiBaseFetchInterceptor(): void {
  if (installed) return;
  if (!API_BASE) return; // No reason to wrap fetch when there's nothing to rewrite.
  if (typeof globalThis.fetch !== "function") return;

  const originalFetch = globalThis.fetch.bind(globalThis);
  globalThis.fetch = ((
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    if (isRelativeApiPath(input)) {
      return originalFetch(rewriteApiUrl(input), init);
    }
    return originalFetch(input, init);
  }) as typeof fetch;

  installed = true;
}

/**
 * Test-only escape hatch: reset the installation flag so a unit test
 * can exercise installation logic against a fresh global fetch stub.
 * Not exported from a barrel file; only the unit test imports it.
 */
export function __resetApiBaseInterceptorForTests(): void {
  installed = false;
}

/**
 * The configured production API origin (without trailing slash), or
 * the empty string when unset. Useful for code that needs to build
 * absolute API URLs for non-fetch consumers (e.g. EventSource, image
 * srcs, anchor hrefs).
 */
export function getApiBaseUrl(): string {
  return API_BASE;
}
